export type AvgVideoStatus = 'draft' | 'review' | 'scheduled' | 'published' | 'archived';

export interface AvgVideoVideo {
  id: string;
  title: string;
  description: string | null;
  annotation: string | null;
  status: AvgVideoStatus;
  video_url: string | null;
  thumbnail_url: string | null;
  /** Раскрытая сервером публичная ссылка Диска для постера; в форму админки не подставляется. */
  thumbnail_display_url?: string | null;
  preview_url: string | null;
  /** Единственный источник длительности; подпись собирает `formatDuration()`. */
  duration_seconds: number | null;
  tags: string[];
  /** Текстовая подпись маршрута. Открыть конкретный маршрут по id экран
   *  «Примеры» не умеет, поэтому идентификатора у карточки больше нет. */
  example_label: string | null;
  is_featured: boolean;
  is_free_access: boolean;
  view_count: number;
  sort_order: number;
  published_at: number | null;
  scheduled_at: number | null;
  created_at: number;
  updated_at: number;
  rubric_ids: string[];
  access: 'ok' | 'locked';
  /* Размещение на карте каталога (контракт v1, AV-ADR-2). Сервер отдаёт поля
     всегда (null — «не задано»); в типе они опциональны, чтобы клиент пережил
     ответ бэкенда без размещения — читать через `?? null` / `?? []`. */
  series_id?: string | null;
  season_id?: string | null;
  episode_position?: number | null;
  collection_ids?: string[];
  related_video_ids?: string[];
}

/** Сезон сериала (контракт v1). Существует только у коллекций kind='series'.
 *  Служебные поля опциональны: клиент строит сезоны и из усечённых ответов
 *  (в `context.series.seasons` приходят только id/номер/название/счётчик). */
export interface AvgVideoSeason {
  id: string;
  collection_id?: string;
  number: number;
  title: string | null;
  item_count: number;
  created_at?: number;
  updated_at?: number;
}

export interface AvgVideoCollection {
  id: string;
  kind: 'series' | 'thematic';
  title: string;
  description: string | null;
  /** Состав приходит целиком, поэтому счётчик коллекции — это его длина. */
  video_ids: string[];
  /** Только для kind === 'series'; список приходит со счётчиками серий. */
  seasons?: AvgVideoSeason[];
}

/** Элемент состава коллекции (контракт v1): видео + сезон + позиция. */
export interface AvgVideoCollectionItem {
  video_id: string;
  season_id: string | null;
  position: number;
}

/** Ответ `GET /api/avg-video/collections/:id`. */
export interface AvgVideoCollectionDetail {
  collection: AvgVideoCollection;
  videos: AvgVideoVideo[];
  items: AvgVideoCollectionItem[];
}

export interface AvgVideoSeriesContext {
  collection: AvgVideoCollection | null;
  season: AvgVideoSeason | null;
  seasons: AvgVideoSeason[];
  /** Серии ТЕКУЩЕГО сезона, по возрастанию position. */
  episodes: AvgVideoVideo[];
  prev: AvgVideoVideo | null;
  next: AvgVideoVideo | null;
}

export interface AvgVideoTopicContext {
  collection: AvgVideoCollection;
  videos: AvgVideoVideo[];
}

/** Ответ `GET /api/avg-video/videos/:id/context`. */
export interface AvgVideoContext {
  video: AvgVideoVideo | null;
  series: AvgVideoSeriesContext | null;
  collections: AvgVideoTopicContext[];
  related: AvgVideoVideo[];
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function asVideo(value: unknown): AvgVideoVideo | null {
  const v = value as AvgVideoVideo | null | undefined;
  return v && typeof v === 'object' && typeof v.id === 'string' ? v : null;
}

/**
 * Текст без разметки — для мест, где строка обрезается до пары строк.
 *
 * Markdown там показывать нельзя: заголовки и списки в обрезке разъезжаются, а
 * сырые звёздочки читаются как опечатка. Поэтому разметку снимаем, а не рисуем.
 */
export function markdownToPlain(value: string | null | undefined): string {
  return (value || '')
    .replace(/```[\s\S]*?```/g, ' ')          // блоки кода целиком
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1') // картинка → подпись
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')  // ссылка → текст
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s{0,3}>\s?/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Список из ответа API — независимо от того, пришёл он конвертом
 * `{ items, total }` или голым массивом.
 *
 * Конверт в модуле не везде одинаков (видео админки отдаются массивом,
 * подборки и рубрики — конвертом), и разбор «как массив» уже дважды ломал
 * экраны: присвоенный объект падал на первом же `.filter`. Разбирать ответ
 * руками в каждом месте больше нельзя — только через этот помощник.
 */
export function parseItems<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  const envelope = raw as { items?: unknown } | null | undefined;
  return Array.isArray(envelope?.items) ? envelope.items as T[] : [];
}

/** Коллекция из любого ответа: недостающие поля добираются безопасными значениями. */
export function normalizeCollection(raw: unknown): AvgVideoCollection | null {
  const c = raw as Partial<AvgVideoCollection> | null | undefined;
  if (!c || typeof c !== 'object' || typeof c.id !== 'string') return null;
  return {
    id: c.id,
    kind: c.kind === 'series' ? 'series' : 'thematic',
    title: typeof c.title === 'string' ? c.title : 'Без названия',
    description: typeof c.description === 'string' ? c.description : null,
    video_ids: asArray<string>(c.video_ids),
    seasons: asArray<AvgVideoSeason>(c.seasons)
      .filter((s) => s && typeof s.id === 'string')
      .map((s) => ({
        ...s,
        number: typeof s.number === 'number' ? s.number : 0,
        title: typeof s.title === 'string' ? s.title : null,
        item_count: typeof s.item_count === 'number' ? s.item_count : 0,
      })),
  };
}

/**
 * Разбор `GET /collections/:id`.
 *
 * Контракт v1 отдаёт `{ collection, videos, items }`. Тот же разбор понимает и
 * «голую» коллекцию (форма до контракта) — иначе экран подборки пустеет на
 * стенде, где бэкенд ещё не обновлён.
 */
export function normalizeCollectionDetail(raw: unknown): AvgVideoCollectionDetail | null {
  const data = raw as { collection?: unknown; videos?: unknown; items?: unknown } | null | undefined;
  if (!data || typeof data !== 'object') return null;
  const collection = normalizeCollection('collection' in data && data.collection ? data.collection : data);
  if (!collection) return null;

  const videos = asArray<unknown>(data.videos).map(asVideo).filter((v): v is AvgVideoVideo => !!v);
  const items = asArray<Partial<AvgVideoCollectionItem>>(data.items)
    .filter((i) => i && typeof i.video_id === 'string')
    .map((i, idx) => ({
      video_id: i.video_id as string,
      season_id: typeof i.season_id === 'string' ? i.season_id : null,
      position: typeof i.position === 'number' ? i.position : idx,
    }));

  // Состав не приехал — восстанавливаем его из порядка video_ids коллекции.
  const fallbackItems = items.length > 0
    ? items
    : collection.video_ids.map((video_id, idx) => ({ video_id, season_id: null, position: idx }));

  return { collection, videos, items: fallbackItems };
}

/** Разбор `GET /videos/:id/context` — любое отсутствующее звено становится пустым. */
export function normalizeVideoContext(raw: unknown): AvgVideoContext {
  const data = raw as {
    video?: unknown;
    series?: unknown;
    collections?: unknown;
    related?: unknown;
  } | null | undefined;

  const empty: AvgVideoContext = { video: null, series: null, collections: [], related: [] };
  if (!data || typeof data !== 'object') return empty;

  const seriesRaw = data.series as {
    collection?: unknown;
    season?: unknown;
    seasons?: unknown;
    episodes?: unknown;
    prev?: unknown;
    next?: unknown;
  } | null | undefined;

  const seriesCollection = seriesRaw ? normalizeCollection(seriesRaw.collection) : null;
  const series: AvgVideoSeriesContext | null = seriesRaw && seriesCollection
    ? {
      collection: seriesCollection,
      season: (seriesRaw.season as AvgVideoSeason | null) || null,
      seasons: asArray<AvgVideoSeason>(seriesRaw.seasons).filter((s) => s && typeof s.id === 'string'),
      episodes: asArray<unknown>(seriesRaw.episodes).map(asVideo).filter((v): v is AvgVideoVideo => !!v),
      prev: asVideo(seriesRaw.prev),
      next: asVideo(seriesRaw.next),
    }
    : null;

  const collections = asArray<{ collection?: unknown; videos?: unknown }>(data.collections)
    .map((entry) => {
      const collection = normalizeCollection(entry?.collection);
      if (!collection) return null;
      return {
        collection,
        videos: asArray<unknown>(entry?.videos).map(asVideo).filter((v): v is AvgVideoVideo => !!v),
      };
    })
    .filter((entry): entry is AvgVideoTopicContext => !!entry);

  return {
    video: asVideo(data.video),
    series,
    collections,
    related: asArray<unknown>(data.related).map(asVideo).filter((v): v is AvgVideoVideo => !!v),
  };
}

/** «2 сезона · 18 серий» / «12 видео» — состав сущности одной строкой. */
export function collectionCompositionLabel(collection: AvgVideoCollection): string {
  const videos = collection.video_ids.length;
  if (collection.kind !== 'series') {
    return `${videos} ${plural(videos, 'видео', 'видео', 'видео')}`;
  }
  const seasons = collection.seasons?.length || 0;
  const episodes = `${videos} ${plural(videos, 'серия', 'серии', 'серий')}`;
  if (seasons > 0) {
    return `${seasons} ${plural(seasons, 'сезон', 'сезона', 'сезонов')} · ${episodes}`;
  }
  return episodes;
}

export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = Math.abs(n) % 10;
  const mod100 = Math.abs(n) % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

export function seasonLabel(season: AvgVideoSeason): string {
  const base = `Сезон ${season.number || ''}`.trim();
  return season.title?.trim() ? `${base} · ${season.title.trim()}` : base;
}

/** Номер серии для показа зрителю. `episode_position` в базе нульбазовая —
 *  наружу она никогда не идёт как есть, иначе сезон начинается с «Серия 0». */
export function episodeNumber(video: AvgVideoVideo, index: number): number {
  return (video.episode_position ?? index) + 1;
}

export interface AvgVideoRubric {
  id: string;
  parent_id: string | null;
  title: string;
  sort_order: number;
  published_count: number;
}

/**
 * Цвет бейджа направления.
 *
 * Направление — корневая рубрика видео, а рубрики заводит куратор: фиксированной
 * таблицы «рубрика → цвет» быть не может. Цвет берётся из палитры по стабильному
 * хешу идентификатора, поэтому одна и та же рубрика всегда одного цвета — и
 * между перерисовками, и между сессиями, — а новая рубрика получает свой без
 * правки кода.
 */
const RUBRIC_ACCENT_COUNT = 6;

export function rubricAccentClass(rubricId: string): string {
  let hash = 0;
  for (let i = 0; i < rubricId.length; i += 1) {
    // FNV-подобная свёртка: |0 держит результат в 32-битном целом.
    hash = (hash * 31 + rubricId.charCodeAt(i)) | 0;
  }
  return `atv-accent-${Math.abs(hash) % RUBRIC_ACCENT_COUNT + 1}`;
}

/** «18:42» / «1:05:03». Длительности нет — подписи тоже нет. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return '';
  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
}

export const AVG_VIDEO_STATUS_LABELS: Record<AvgVideoStatus, string> = {
  draft: 'Черновик',
  review: 'На проверке',
  scheduled: 'Запланировано',
  published: 'Опубликовано',
  archived: 'Архив',
};

export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * То же для значения атрибута: `innerHTML` кавычки не экранирует, поэтому
 * название с `"` рвало бы разметку у `aria-label` и `data-*`.
 */
export function escapeAttr(text: string): string {
  return escapeHtml(text).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export function formatVideoDate(ts: number | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function formatViewCount(n: number): string {
  return n.toLocaleString('ru-RU');
}

export function tagsLine(tags: string[]): string {
  return tags.map((t) => (t.startsWith('#') ? t : `#${t}`)).join(' ');
}

const PUBLIC_DISK_RE = /^https?:\/\/(?:disk\.yandex\.(?:ru|com|kz|by|uz)|yadi\.sk)\/(?:i|d)\/\S+$/i;

/** Публичная ссылка «Поделиться» с Яндекс.Диска: играет только после раскрытия на сервере. */
export function isPublicDiskUrl(u: string | null | undefined): boolean {
  return typeof u === 'string' && PUBLIC_DISK_RE.test(u.trim());
}

/** Ссылка на видео, которую сам браузер открыть не может — нужен запрос `/play`. */
export function needsServerPlayback(u: string | null | undefined): boolean {
  return !!u?.startsWith('disk:/') || isPublicDiskUrl(u);
}

/**
 * Картинка постера: раскрытая сервером ссылка приоритетнее сырой.
 *
 * Ссылку на Диск в `<img>` не отдаём ни в каком виде — ни публичную (Диск с
 * чужой страницы иногда отбивает 403), ни приватную `disk:/…` (браузер такую
 * схему не понимает вовсе и рисует битую картинку). Обе обязаны приезжать
 * раскрытыми в `thumbnail_display_url`; не приехали — показываем запасную
 * заглушку, а не заведомо нерабочий `src`.
 */
export function posterSrc(video: AvgVideoVideo): string | null {
  const display = video.thumbnail_display_url?.trim();
  if (display) return display;
  const raw = video.thumbnail_url?.trim();
  if (!raw || isPublicDiskUrl(raw) || raw.startsWith('disk:/')) return null;
  return raw;
}

export interface VideoEmbedResolved {
  embedSrc: string;
  watchUrl: string;
  kind: 'youtube' | 'vk' | 'file' | 'iframe';
  needsVkHash?: boolean;
}

/** Преобразует ссылку на видео в URL для iframe/video. */
export function resolveVideoEmbedSrc(rawUrl: string): VideoEmbedResolved | null {
  let url = rawUrl.trim();
  if (!url) return null;

  const iframeSrc = url.match(/src=["']([^"']+)["']/i);
  if (iframeSrc) url = iframeSrc[1];

  // Свой путь (поток с Диска, /media/…) — всегда файл для <video>.
  if (url.startsWith('/')) {
    return { embedSrc: url, watchUrl: url, kind: 'file' };
  }

  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const host = parsed.hostname.replace(/^www\./, '').toLowerCase();

  // Прямая (временная) ссылка Яндекс.Диска — обычный файл для <video>, а не iframe:
  // расширения в пути нет, оно спрятано в query, поэтому проверка ниже её не ловит.
  if (host === 'downloader.disk.yandex.ru' || host.endsWith('storage.yandex.net')) {
    return { embedSrc: url, watchUrl: url, kind: 'file' };
  }

  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/i);
  if (ytMatch) {
    const id = ytMatch[1];
    return {
      embedSrc: `https://www.youtube.com/embed/${id}`,
      watchUrl: url,
      kind: 'youtube',
    };
  }

  if (/video_ext\.php/i.test(parsed.pathname)) {
    const oid = parsed.searchParams.get('oid');
    const id = parsed.searchParams.get('id');
    if (oid && id) {
      const params = new URLSearchParams();
      params.set('oid', oid);
      params.set('id', id);
      const hash = parsed.searchParams.get('hash');
      if (hash) params.set('hash', hash);
      if (!params.has('hd')) params.set('hd', '2');
      const embedSrc = `https://vkvideo.ru/video_ext.php?${params.toString()}`;
      const watchUrl = `https://vk.com/video${oid}_${id}`;
      return { embedSrc, watchUrl, kind: 'vk', needsVkHash: !hash };
    }
  }

  const dashMatch = url.match(/\/video-(\d+)_(\d+)/i);
  if (dashMatch && (host.includes('vk.com') || host.includes('vkvideo.ru'))) {
    const oid = `-${dashMatch[1]}`;
    const id = dashMatch[2];
    return {
      embedSrc: `https://vkvideo.ru/video_ext.php?oid=${oid}&id=${id}&hd=2`,
      watchUrl: url,
      kind: 'vk',
      needsVkHash: true,
    };
  }

  const userMatch = url.match(/\/video(\d+)_(\d+)/i);
  if (userMatch && (host.includes('vk.com') || host.includes('vkvideo.ru'))) {
    const oid = userMatch[1];
    const id = userMatch[2];
    return {
      embedSrc: `https://vkvideo.ru/video_ext.php?oid=${oid}&id=${id}&hd=2`,
      watchUrl: url,
      kind: 'vk',
      needsVkHash: true,
    };
  }

  if (/\.(mp4|webm|ogg)(\?|$)/i.test(url)) {
    return { embedSrc: url, watchUrl: url, kind: 'file' };
  }

  if (host.includes('vk.com') || host.includes('vkvideo.ru')) {
    return null;
  }

  return { embedSrc: url, watchUrl: url, kind: 'iframe' };
}

/** URL для короткого превью при наведении (без звука). */
export function buildHoverPreviewSrc(resolved: VideoEmbedResolved): string | null {
  if (resolved.kind === 'file') return resolved.embedSrc;

  try {
    const url = new URL(resolved.embedSrc);
    if (resolved.kind === 'youtube') {
      url.searchParams.set('autoplay', '1');
      url.searchParams.set('mute', '1');
      url.searchParams.set('controls', '0');
      url.searchParams.set('modestbranding', '1');
      url.searchParams.set('rel', '0');
      url.searchParams.set('playsinline', '1');
      return url.toString();
    }
    if (resolved.kind === 'vk') {
      url.searchParams.set('autoplay', '1');
      if (!url.searchParams.has('hd')) url.searchParams.set('hd', '2');
      return url.toString();
    }
    url.searchParams.set('autoplay', '1');
    return url.toString();
  } catch {
    return resolved.embedSrc;
  }
}

export function canHoverPreview(video: AvgVideoVideo): boolean {
  if (!video.video_url?.trim()) return false;
  return resolveVideoEmbedSrc(video.video_url) !== null;
}
